import { Box, Text } from "@chakra-ui/react";
import { motion, type Variants } from "framer-motion";

const MotionDiv = motion.div;

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const headerVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};

const stripVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.0, ease, delay: 0.35 },
  },
};

const brands = [
  { src: "/brands/CBMM.webp", alt: "CBMM" },
  { src: "/brands/DCC.webp", alt: "Dave Collision" },
  { src: "/brands/HAITONG.webp", alt: "Haitong" },
  { src: "/brands/JAFCO.webp", alt: "JAFCO Asia" },
  { src: "/brands/L&L.webp", alt: "Lee & Lee" },
  { src: "/brands/MDD.webp", alt: "MDD" },
];

// Triplicate so the loop has enough runway on all screen widths
const track = [...brands, ...brands, ...brands];

export default function BrandCarousel() {
  return (
    <Box
      w="100vw"
      position="relative"
      left="50%"
      ml="-50vw"
      pt={{ base: "2.5rem", tablet: "3rem", desktop: "3.5rem" }}
      pb={{ base: "2.75rem", tablet: "3.25rem", desktop: "3.75rem" }}
    >
      <style>{`
        @keyframes brandScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.3333%); }
        }
        .brand-track {
          display: flex;
          align-items: center;
          gap: 4.5rem;
          width: max-content;
          animation: brandScroll 28s linear infinite;
        }
        .brand-track:hover {
          animation-play-state: paused;
        }
        .brand-logo {
          height: 38px;
          width: auto;
          max-width: 130px;
          object-fit: contain;
          flex-shrink: 0;
          filter: grayscale(100%) opacity(0.5);
          transition: filter 0.3s ease;
        }
        .brand-logo:hover {
          filter: grayscale(0%) opacity(1);
          transition: filter 0.3s ease, transform 0.3s ease;
          transform: translateY(-2px);
        }
      `}</style>

      {/* Section header */}
      <MotionDiv
        variants={headerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        style={{ textAlign: "center", marginBottom: "2rem" }}
      >
        <Box px={{ base: "1.5rem", tablet: 0 }}>
          <Text
            color="primary.surface.default"
            fontWeight="800"
            letterSpacing="0.08em"
            fontSize={{ base: "1.2rem", tablet: "1.4rem", desktop: "1.5rem" }}
          >
            Trusted &amp; Recognised By
          </Text>
          <Box
            mt="0.9rem"
            mx="auto"
            w={{ base: "60px", tablet: "78px" }}
            h="3px"
            rounded="full"
            bg="linear-gradient(90deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,1) 100%)"
          />
          <Text
            mt="1rem"
            fontWeight="800"
            fontSize={{ base: "2rem", tablet: "2.5rem", desktop: "3.05rem" }}
            color="greyscale.text.title"
            lineHeight="1.08"
            letterSpacing="-0.02em"
          >
            Leading Office Brands
          </Text>
          <Text
            mt="0.95rem"
            maxW="700px"
            mx="auto"
            color="greyscale.text.body"
            fontSize={{ base: "0.96rem", tablet: "1rem", desktop: "1.02rem" }}
            lineHeight="1.85"
          >
            Trusted by offices, businesses, and established organisations that
            value reliable service quality and professional care.
          </Text>
        </Box>
      </MotionDiv>

      {/* Carousel strip */}
      <MotionDiv
        variants={stripVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
      >
        <Box position="relative" overflow="hidden" py="10px" my="-10px">
          {/* Left fade */}
          <Box
            position="absolute"
            top="0"
            left="0"
            h="100%"
            w={{ base: "120px", tablet: "250px", desktop: "350px" }}
            zIndex={2}
            pointerEvents="none"
            style={{
              background:
                "linear-gradient(to right, #ffffff 0%, transparent 100%)",
            }}
          />
          {/* Right fade */}
          <Box
            position="absolute"
            top="0"
            right="0"
            h="100%"
            w={{ base: "120px", tablet: "250px", desktop: "350px" }}
            zIndex={2}
            pointerEvents="none"
            style={{
              background:
                "linear-gradient(to left, #ffffff 0%, transparent 100%)",
            }}
          />

          <div className="brand-track">
            {track.map((brand, i) => (
              <img
                key={i}
                src={brand.src}
                alt={brand.alt}
                className="brand-logo"
              />
            ))}
          </div>
        </Box>
      </MotionDiv>
    </Box>
  );
}
